import { concat } from "lodash";
import { createSelector } from "@reduxjs/toolkit";

import { LearnerProgress, Skill } from "@hubcapp/types";

import { RootState } from "../../store";

const selectLearnerState = (state: RootState) => state.learner;

export const selectProgress = createSelector(
  selectLearnerState,
  learnerState => learnerState.progress,
);

export const selectMasterProgress = createSelector(
  selectLearnerState,
  learnerState => learnerState.masterProgress,
);

export const selectCanEditProgress = createSelector(
  selectLearnerState,
  () => true,
);

export const selectProgressCompletion = createSelector(
  selectLearnerState,
  learnerState => learnerState.progress?.overallProgress || 0,
);

const getSkillCategory = (skillCategoryId: string, lProg: LearnerProgress) =>
  (lProg.categories || []).find(cat => cat.id === skillCategoryId);

export const selectSkillCategorySkillIds = (skillCategoryId: string) =>
  createSelector(
    selectLearnerState,
    learnerState =>
      (
        getSkillCategory(
          skillCategoryId,
          (learnerState.progress || {}) as LearnerProgress,
        )?.skills || []
      )
        .map(x => x?.id || "")
        .filter(x => Boolean(x)) || [],
  );

export const selectSkillCategoryCompletionPercentage = (
  skillCategoryId: string,
) =>
  createSelector(
    selectLearnerState,
    learnerState =>
      getSkillCategory(
        skillCategoryId,
        (learnerState.progress || {}) as LearnerProgress,
      )?.progressPercentage || 0,
  );

export const selectSkillCategoryTitle = (skillCategoryId: string) =>
  createSelector(
    selectLearnerState,
    learnerState =>
      getSkillCategory(
        skillCategoryId,
        (learnerState.progress || {}) as LearnerProgress,
      )?.categoryName || 0,
  );

const getSkill = (skillId: string, lProg: LearnerProgress) => {
  let skills: Skill[] = [];

  lProg.categories.forEach(cat => {
    skills = concat(skills, cat.skills as Skill[]);
  });

  return skills.find(skill => skill.id === skillId);
};

export const selectSkillById = (skillId: string) =>
  createSelector(selectLearnerState, learnerState =>
    getSkill(skillId, (learnerState.progress || {}) as LearnerProgress),
  );

export const selectSkillScoreChange = (skillId: string) =>
  createSelector(selectLearnerState, learnerState => {
    const skill = getSkill(
      skillId,
      (learnerState.progress || {}) as LearnerProgress,
    );
    const masterSkill = getSkill(
      skillId,
      (learnerState.masterProgress || []) as LearnerProgress,
    );

    return (skill?.score || 0) - (masterSkill?.score || 0);
  });

export const selectAllSkills = createSelector(
  selectLearnerState,
  learnerState => {
    const skills: Skill[] = [];
    if (learnerState.progress?.categories) {
      // This is a flat map for skills (Older versions of IOS and Android don't support flatMap)
      for (const category of learnerState.progress?.categories) {
        category.skills.forEach(skill => {
          if (skill) {
            skills.push(skill);
          }
        });
      }
    }

    return skills;
  },
);
